import React, { useState, useMemo, useCallback } from 'react';
import { Gift, Plus, TrendingUp, Users, RefreshCw } from 'lucide-react';
import { WeddingInvitation } from '../types/invitation';
import { giftService } from '../lib/supabase';
import { createLogger } from '../lib/logger';
import { useAuth } from '../contexts/AuthContext';
import { useSearch } from '../hooks/useSearch';
import { useCSVExport } from '../hooks/useCSVExport';
import useCollaborationPermissions from '../hooks/useCollaborationPermissions';
import ThemedCard from './themed/ThemedCard';
import InvitationContextBanner from './InvitationContextBanner';
import { useGiftData } from './gift/hooks/useGiftData';
import { useGiftForm } from './gift/hooks/useGiftForm';
import { useBatchGiftEntry } from './gift/hooks/useBatchGiftEntry';
import { GiftTabId } from './gift/types';
import { OverviewTab } from './gift/components/OverviewTab';
import { RecordsTab } from './gift/components/RecordsTab';
import { BulkEntryTab } from './gift/components/BulkEntryTab';
import { AddEditFormTab } from './gift/components/AddEditFormTab';

const log = createLogger('gift');

interface GiftTrackerProps {
  inviteCode: string;
  invitationId: string;
  refreshTrigger?: number;
  currentInvitation?: WeddingInvitation;
}

export const GiftTracker: React.FC<GiftTrackerProps> = ({
  inviteCode,
  invitationId,
  refreshTrigger,
  currentInvitation,
}) => {
  const { user } = useAuth();
  const { canEdit, loading: permissionsLoading } = useCollaborationPermissions(currentInvitation?.id || null);
  const [activeTab, setActiveTab] = useState<GiftTabId>('overview');

  const { giftRecords, giftStats, rsvpResponses, loading, refresh } = useGiftData(
    invitationId,
    inviteCode,
    user?.id,
    refreshTrigger
  );

  const { searchTerm, setSearchTerm } = useSearch();
  const { exportingData, setExportingData, downloadCSV } = useCSVExport();

  const navigateToRecords = useCallback(() => setActiveTab('records'), []);

  const {
    showAddForm,
    setShowAddForm,
    editingRecord,
    formData,
    setFormData,
    formErrors,
    saving,
    deleting,
    resetForm,
    handleSubmit,
    handleEdit,
    handleDelete,
    fillFromRSVP,
  } = useGiftForm({
    invitationId,
    onSaved: refresh,
    onNavigateToRecords: navigateToRecords,
  });

  const {
    batchEntries,
    loadingBatchData,
    batchProcessing,
    loadBatchData,
    updateBatchEntryAmount,
    updateBatchEntryMessage,
    handleBatchSubmit,
  } = useBatchGiftEntry({
    invitationId,
    onRefresh: refresh,
  });

  const filteredRecords = useMemo(
    () =>
      giftRecords.filter(
        record =>
          record.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (record.guestEmail && record.guestEmail.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [giftRecords, searchTerm]
  );

  const handleExport = useCallback(async () => {
    try {
      setExportingData(true);
      const csvContent = await giftService.exportGiftRecordsToCSV(invitationId);
      downloadCSV(csvContent, `紅包記錄_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      log.error('匯出失敗:', error);
      alert(`匯出失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setExportingData(false);
    }
  }, [invitationId, setExportingData, downloadCSV]);

  const handleTabClick = useCallback(
    (tabId: GiftTabId) => {
      setActiveTab(tabId);
      if (tabId === 'add' && !showAddForm) {
        resetForm();
        setShowAddForm(true);
      } else if (tabId === 'bulk' && batchEntries.length === 0) {
        loadBatchData();
      }
    },
    [showAddForm, resetForm, setShowAddForm, batchEntries.length, loadBatchData]
  );

  const handleEditRecord = useCallback(
    (record: Parameters<typeof handleEdit>[0]) => {
      handleEdit(record);
      setActiveTab('add');
    },
    [handleEdit]
  );

  const handleDeleteRecord = useCallback(
    (recordId: string) => handleDelete(recordId, refresh),
    [handleDelete, refresh]
  );

  const handleFormCancel = useCallback(() => {
    resetForm();
    setShowAddForm(false);
    setActiveTab('records');
  }, [resetForm, setShowAddForm]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-wedding-pink" />
        <span className="ml-2 text-wedding-text-body">載入中...</span>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: '紅包總覽', icon: TrendingUp },
    { id: 'records' as const, label: '記錄列表', icon: Gift },
    { id: 'bulk' as const, label: '批量登記', icon: Users },
    { id: 'add' as const, label: '新增記錄', icon: Plus },
  ];

  return (
    <div className="space-y-6">
      <InvitationContextBanner invitation={currentInvitation || null} />

      {permissionsLoading && (
        <div className="text-center py-4">
          <div className="text-wedding-text-body">檢查權限中...</div>
        </div>
      )}

      {!permissionsLoading && !canEdit && (
        <ThemedCard className="p-6 text-center">
          <div className="text-wedding-text-body">
            您沒有權限管理此邀請函的紅包記錄。請聯絡邀請函擁有者。
          </div>
        </ThemedCard>
      )}

      {canEdit && (
        <>
          <div className="flex space-x-1 bg-wedding-pink/5 p-1 rounded-lg">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-wedding-pink text-white'
                    : 'text-wedding-text-body hover:bg-wedding-pink/10'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <OverviewTab
              giftStats={giftStats}
              giftRecords={giftRecords}
              loading={loading}
              exporting={exportingData}
              onRefresh={refresh}
              onExport={handleExport}
            />
          )}

          {activeTab === 'records' && (
            <RecordsTab
              giftRecords={giftRecords}
              filteredRecords={filteredRecords}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              deleting={deleting}
              onEdit={handleEditRecord}
              onDelete={handleDeleteRecord}
            />
          )}

          {activeTab === 'bulk' && (
            <BulkEntryTab
              batchEntries={batchEntries}
              loadingBatchData={loadingBatchData}
              batchProcessing={batchProcessing}
              onLoadBatchData={loadBatchData}
              onBatchSubmit={handleBatchSubmit}
              onUpdateAmount={updateBatchEntryAmount}
              onUpdateMessage={updateBatchEntryMessage}
              onSetActiveTab={setActiveTab}
            />
          )}

          {activeTab === 'add' && (
            <AddEditFormTab
              editingRecord={editingRecord}
              formData={formData}
              setFormData={setFormData}
              formErrors={formErrors}
              saving={saving}
              rsvpResponses={rsvpResponses}
              onSubmit={handleSubmit}
              onCancel={handleFormCancel}
              onFillFromRSVP={fillFromRSVP}
            />
          )}
        </>
      )}
    </div>
  );
};

export default GiftTracker;
